import { Container, Graphics, Text, TextStyle } from 'pixi.js-legacy';
import { CONFIG } from '../config';
import { GameState } from '../core/Game';
import { PixiRenderer } from '../pixi/PixiRenderer';
import { BackgroundRenderer } from '../pixi/ui/BackgroundRenderer';

interface SoundManagerLike {
    unlockAudio(): void;
}

interface InputManagerLike {
    cancelTouchConfirm(): void;
    isButtonPressed(button: string): boolean;
}

interface BackgroundState {
    hue: number;
    angle: number;
}

interface GameLike {
    getRenderer(): PixiRenderer;
    getSoundManager(): SoundManagerLike;
    getInputManager(): InputManagerLike;
    getBackgroundState(): BackgroundState;
    setState(state: GameState): void;
}

type MenuItem = 'start' | 'options' | 'achievements';

export class TitleScreen {
    private game: GameLike;
    private starAngle: number;
    private inputHandler: ((e: MouseEvent | TouchEvent) => void) | null;
    private selectedIndex: number;
    private menuItems: MenuItem[];
    private lastTouchTime: number;

    // PixiJS
    private pixiRenderer: PixiRenderer | null = null;
    private container: Container | null = null;
    private backgroundRenderer: BackgroundRenderer | null = null;
    private titleText: Text | null = null;
    private startText: Text | null = null;
    private optionsText: Text | null = null;
    private achievementsText: Text | null = null;
    private starGraphics: Graphics | null = null;

    constructor(game: GameLike) {
        this.game = game;
        this.starAngle = 0;
        this.inputHandler = null;
        this.selectedIndex = 0;
        this.menuItems = ['start', 'options', 'achievements'];
        this.lastTouchTime = 0;
    }

    setPixiRenderer(renderer: PixiRenderer): void {
        this.pixiRenderer = renderer;
    }

    setupInputListener(): void {
        const canvas = this.pixiRenderer?.getCanvas() || document.getElementById('gameCanvas') as HTMLCanvasElement | null;
        if (!canvas) return;

        if (this.inputHandler) return;

        this.lastTouchTime = 0;

        this.inputHandler = (e: MouseEvent | TouchEvent) => {
            if (e.type === 'click' && Date.now() - this.lastTouchTime < 300) {
                return;
            }
            if (e.type === 'touchend') {
                this.lastTouchTime = Date.now();
            }

            const renderer = this.game.getRenderer();
            const rect = canvas.getBoundingClientRect();
            const scaleX = renderer.getCanvasWidth() / rect.width;
            const scaleY = renderer.getCanvasHeight() / rect.height;

            let clientX: number, clientY: number;
            if (e.type === 'touchend') {
                const touchEvent = e as TouchEvent;
                if (touchEvent.changedTouches.length === 0) return;
                clientX = touchEvent.changedTouches[0].clientX;
                clientY = touchEvent.changedTouches[0].clientY;
            } else {
                const mouseEvent = e as MouseEvent;
                clientX = mouseEvent.clientX;
                clientY = mouseEvent.clientY;
            }

            const x = (clientX - rect.left) * scaleX;
            const y = (clientY - rect.top) * scaleY;

            const canvasWidth = renderer.getCanvasWidth();
            const canvasHeight = renderer.getCanvasHeight();

            // START button area
            const startY = canvasHeight / 2 + 120;
            if (y >= startY - 30 && y <= startY + 30 && x >= canvasWidth / 2 - 100 && x <= canvasWidth / 2 + 100) {
                this.selectedIndex = 0;
                this.executeSelectedMenu();
                return;
            }

            // Bottom buttons area
            if (y >= canvasHeight - 80) {
                if (x >= 15 && x <= 200) {
                    this.selectedIndex = 1;
                    this.executeSelectedMenu();
                    return;
                }
                if (x >= canvasWidth - 250 && x <= canvasWidth - 15) {
                    this.selectedIndex = 2;
                    this.executeSelectedMenu();
                    return;
                }
            }
        };

        canvas.addEventListener('touchend', this.inputHandler);
        canvas.addEventListener('click', this.inputHandler);
    }

    init(): void {
        this.starAngle = 0;
        this.selectedIndex = 0;
        this.setupInputListener();
        this.initPixiUI();
    }

    private initPixiUI(): void {
        if (!this.pixiRenderer) return;

        // Clean up existing container if any
        if (this.container) {
            this.container.destroy({ children: true });
            this.container = null;
        }

        const layers = this.pixiRenderer.getLayers();
        this.container = new Container();
        layers.ui.addChild(this.container);

        // Background
        this.backgroundRenderer = new BackgroundRenderer(layers.background);

        const width = this.pixiRenderer.getCanvasWidth();
        const height = this.pixiRenderer.getCanvasHeight();

        // Title text
        const titleStyle = new TextStyle({
            fontFamily: CONFIG.UI.FONT_FAMILY,
            fontSize: CONFIG.UI.TITLE_SIZE,
            fontWeight: '100',
            fill: '#FFFFFF',
            align: 'center',
        });
        this.titleText = new Text('Spectrum Survive', titleStyle);
        this.titleText.anchor.set(0.5);
        this.titleText.position.set(width / 2, height / 3);
        this.container.addChild(this.titleText);

        // Menu button style
        const buttonFontSize = CONFIG.UI.SUBTITLE_SIZE * 0.7;
        const buttonStyle = new TextStyle({
            fontFamily: CONFIG.UI.FONT_FAMILY,
            fontSize: buttonFontSize,
            fontWeight: '100',
            fill: '#FFFFFF',
            align: 'center',
        });

        // START button
        this.startText = new Text('START', buttonStyle);
        this.startText.anchor.set(0.5);
        this.startText.position.set(width / 2, height / 2 + 120);
        this.container.addChild(this.startText);

        // OPTIONS button
        this.optionsText = new Text('OPTIONS', buttonStyle.clone());
        this.optionsText.anchor.set(0, 0.5);
        this.optionsText.position.set(40, height - 50);
        this.container.addChild(this.optionsText);

        // ACHIEVEMENTS button
        this.achievementsText = new Text('ACHIEVEMENTS', buttonStyle.clone());
        this.achievementsText.anchor.set(1, 0.5);
        this.achievementsText.position.set(width - 40, height - 50);
        this.container.addChild(this.achievementsText);

        // Selection star
        this.starGraphics = new Graphics();
        this.container.addChild(this.starGraphics);
    }

    update(deltaTime: number): void {
        const input = this.game.getInputManager();
        input.cancelTouchConfirm();

        this.starAngle += deltaTime * 3;

        if (input.isButtonPressed('left') || input.isButtonPressed('up')) {
            this.selectedIndex = (this.selectedIndex - 1 + this.menuItems.length) % this.menuItems.length;
        }
        if (input.isButtonPressed('right') || input.isButtonPressed('down')) {
            this.selectedIndex = (this.selectedIndex + 1) % this.menuItems.length;
        }

        const confirmPressed = input.isButtonPressed('confirm');
        if (confirmPressed) {
            this.executeSelectedMenu();
        }
    }

    executeSelectedMenu(): void {
        const soundManager = this.game.getSoundManager();

        switch (this.menuItems[this.selectedIndex]) {
            case 'start':
                if (soundManager) {
                    soundManager.unlockAudio();
                }
                this.game.setState(GameState.PLAYING);
                break;
            case 'options':
                this.game.setState(GameState.OPTIONS);
                break;
            case 'achievements':
                this.game.setState(GameState.ACHIEVEMENTS);
                break;
        }
    }

    private drawRotatingStar(g: Graphics, x: number, y: number, size: number = 10): void {
        g.clear();
        g.lineStyle(2, 0xFFFFFF);

        // Save position for rotation
        g.position.set(x, y);
        g.rotation = this.starAngle;

        // Draw 6-pointed star lines from center
        for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3;
            g.moveTo(0, 0);
            g.lineTo(Math.cos(angle) * size, Math.sin(angle) * size);
        }
    }

    render(): void {
        if (!this.pixiRenderer || !this.container) return;

        const renderer = this.game.getRenderer();
        const width = renderer.getCanvasWidth();
        const height = renderer.getCanvasHeight();
        const bg = this.game.getBackgroundState();

        // Update background
        if (this.backgroundRenderer) {
            this.backgroundRenderer.render(bg, width, height);
        }

        // Update positions for responsive layout
        if (this.titleText) {
            this.titleText.position.set(width / 2, height / 3);
        }
        if (this.startText) {
            this.startText.position.set(width / 2, height / 2 + 120);
            this.startText.alpha = this.selectedIndex === 0 ? 1 : 0.5;
        }
        if (this.optionsText) {
            this.optionsText.position.set(40, height - 50);
            this.optionsText.alpha = this.selectedIndex === 1 ? 1 : 0.5;
        }
        if (this.achievementsText) {
            this.achievementsText.position.set(width - 40, height - 50);
            this.achievementsText.alpha = this.selectedIndex === 2 ? 1 : 0.5;
        }

        // Update selection star position
        if (this.starGraphics) {
            let starX = 0, starY = 0;

            if (this.selectedIndex === 0 && this.startText) {
                starX = this.startText.x - this.startText.width / 2 - 25;
                starY = this.startText.y;
            } else if (this.selectedIndex === 1 && this.optionsText) {
                starX = 15;
                starY = this.optionsText.y;
            } else if (this.selectedIndex === 2 && this.achievementsText) {
                starX = width - 40 - this.achievementsText.width - 25;
                starY = this.achievementsText.y;
            }

            this.drawRotatingStar(this.starGraphics, starX, starY);
        }
    }

    cleanup(): void {
        const canvas = this.pixiRenderer?.getCanvas() || document.getElementById('gameCanvas') as HTMLCanvasElement | null;
        if (canvas && this.inputHandler) {
            canvas.removeEventListener('touchend', this.inputHandler);
            canvas.removeEventListener('click', this.inputHandler);
            this.inputHandler = null;
        }

        // Cleanup PixiJS objects
        if (this.backgroundRenderer) {
            this.backgroundRenderer.destroy();
            this.backgroundRenderer = null;
        }

        if (this.container) {
            this.container.destroy({ children: true });
            this.container = null;
        }

        this.titleText = null;
        this.startText = null;
        this.optionsText = null;
        this.achievementsText = null;
        this.starGraphics = null;
    }
}
